import {useCallback, useEffect, useRef} from "react";

/**
 * Only happens on updates, not on the first render
 * @param {(function())|*} fn
 * @param {(*|string)[]} dependencies
 */
export const useUpdateEffect = (fn, dependencies = []) => {
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            return fn();
        }
    }, dependencies);
};

/**
 * Debounces a function, ensuring it only runs after the specified delay.
 * Cancels the previous timer if dependencies change.
 *
 * @param {Function} fn - The function to debounce.
 * @param {Array<any>} dependencies - The dependencies array for the effect.
 * @param {number} delay - The debounce delay in milliseconds.
 */
export const useDebouncedEffect = (fn, dependencies = [], delay = 500) => {
    const timerRef = useRef(null);
    
    useUpdateEffect(() => {
        // Start a new timer
        timerRef.current = setTimeout(() => {
            fn();
        }, delay);

        // Cleanup timer on unmount or when dependencies change
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [...dependencies, delay]); // Include delay in dependencies in case it changes
};

// This hook allows you to safely "useEffect" with async functions or promises
// and conditionally perform afterPromise only if the component is still
// mounted when the promise completes. This is useful for situations where
// you want to set the state of a component with the result from a promise
// and allows you to safely avoid the 'Can't perform a React state update on
// an unmounted component' error.
/**
 * @template T
 * @param {(function(): Promise<T>)|Promise<T>} promiseOrFunction
 * @param {function(T)} afterPromise
 * @param {*[]} deps
 * @param conditional
 */
export const useEffectPromise = (promiseOrFunction, afterPromise, deps, conditional) => {
    useEffect(() => {
	    let stillMounted = true;
    
        if (conditional) {
            const promise = (typeof promiseOrFunction === 'function')
                ? promiseOrFunction()
                : promiseOrFunction;
            
            (async () => {
                try {
                    const result = await promise;
                    if (stillMounted) {
                        afterPromise?.(result);
                    }
                } catch (error) {
                    if (stillMounted) {
                        console.error(error);
                    }
                }
            })();
        }
            
        return () => {
            stillMounted = false;
        };
    }, deps);
};
